import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { useSettings } from "@/context/SettingsContext";
import { OfferBanner } from "@/components/OfferBanner";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { HoneypotField, readHoneypot, submitErrorMessage } from "@/components/HoneypotField";
import { ShieldCheck, FileText, Users, Clock, Send, Phone, Check } from "lucide-react";

const MISSIONS = [
  "Estimer la valeur locative de votre bien et vous conseiller sur son positionnement.",
  "Préparer et mettre en valeur le logement avant sa mise en location.",
  "Rédiger et diffuser l’annonce sur les supports adaptés.",
  "Organiser les visites et assurer le suivi des candidats.",
  "Étudier et sélectionner les dossiers locataires avec rigueur, dans le respect de la réglementation.",
  "Vérifier la solvabilité des candidats et sécuriser la mise en location.",
  "Rédiger le bail et assurer les démarches liées à l’entrée du locataire.",
  "Réaliser les états des lieux, avec photos et suivi précis du logement.",
  "Encaisser les loyers et provisions sur charges et assurer le suivi administratif.",
  "Réviser les loyers et effectuer la régularisation des charges.",
  "Suivre les travaux et interventions nécessaires dans le logement.",
  "Gérer les sinistres, de leur déclaration jusqu’au suivi de leur résolution.",
  "Assurer les échanges avec les locataires, artisans, assurances et différents intervenants.",
  "Vous accompagner dans vos obligations de propriétaire, notamment pour la préparation des éléments nécessaires à votre déclaration de revenus fonciers.",
];

const EMPTY_FORM = { name: "", email: "", phone: "", property_type: "", city: "", message: "" };

export const GestionPage = () => {
  const { settings } = useSettings();
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const website = readHoneypot(e);
    setSubmitting(true);
    try {
      const details = [
        formData.property_type && `Type de bien : ${formData.property_type}`,
        formData.city && `Commune : ${formData.city}`,
        formData.message,
      ].filter(Boolean).join("\n");
      await axios.post(`${API}/contacts`, {
        website,
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        subject: "Gestion locative",
        message: details,
      });
      toast.success("Votre demande a bien été envoyée. Nous vous recontactons rapidement !");
      setFormData(EMPTY_FORM);
    } catch (error) {
      toast.error(submitErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="gestion-page">
      <Header />

      <section className="relative py-32 bg-[#0A0A0A]">
        <div className="container-custom text-center">
          <p className="font-cinzel text-sm uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Gestion locative</p>
          <h1 className="font-serif text-4xl md:text-6xl text-white mb-6" data-testid="gestion-title">
            Faire gérer son bien
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Confiez la gestion de votre bien à {settings.agency_name} et percevez vos loyers en toute sérénité.
            Nous nous occupons de tout, du choix du locataire au suivi quotidien.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
            <a href="#gestion-form">
              <Button className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-white px-8" data-testid="gestion-cta-form">
                Demander une estimation locative
              </Button>
            </a>
            <a href={`tel:${settings.phone?.replace(/\s/g, "")}`} className="flex items-center gap-2 text-white hover:text-[#D4AF37] transition-colors" data-testid="gestion-cta-phone">
              <Phone size={18} /> {settings.phone}
            </a>
          </div>

          <div className="mt-12 text-left" data-testid="gestion-offer">
            <OfferBanner ctaOverride="#gestion-form" />
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="section-padding" data-testid="gestion-intro">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <p className="font-cinzel text-sm uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Notre vision</p>
              <h2 className="font-serif text-3xl md:text-4xl leading-tight">
                Votre patrimoine mérite une gestion qui va au-delà de l’encaissement des loyers
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-5 text-gray-600 leading-relaxed">
              <p>Mettre un bien en location est une façon de valoriser et de développer son patrimoine. Mais être propriétaire bailleur implique aussi des démarches, des obligations et parfois des imprévus qui peuvent rapidement devenir chronophages.</p>
              <p>Chez MAINGUY Immobilier, nous considérons que la gestion locative ne se résume pas à percevoir un loyer chaque mois.</p>
              <p className="text-[#0A0A0A] font-medium">Notre rôle est de vous faire gagner du temps, sécuriser votre investissement et vous accompagner dans la durée, avec une gestion rigoureuse, réactive et transparente.</p>
              <p>Implantés au Loroux-Bottereau, nous privilégions une relation de proximité et un interlocuteur direct. Votre bien est suivi avec attention et vous bénéficiez d’un accompagnement adapté à votre situation, que vous soyez propriétaire d’un seul logement ou que vous développiez progressivement votre patrimoine.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Missions */}
      <section className="section-padding bg-[#F9F9F9]" data-testid="gestion-missions">
        <div className="container-custom">
          <div className="max-w-3xl mb-14">
            <p className="font-cinzel text-sm uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Nos missions</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-5">Une gestion locative complète et sur mesure</h2>
            <p className="text-gray-600 leading-relaxed">De la recherche du locataire au suivi quotidien de votre bien, nous prenons en charge les principales démarches nécessaires à sa bonne gestion.</p>
            <p className="text-[#0A0A0A] font-medium mt-3">Nous vous accompagnons notamment pour :</p>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-5">
            {MISSIONS.map((item) => (
              <li key={item} className="flex items-start gap-3 text-gray-700 leading-relaxed">
                <Check size={18} className="text-[#D4AF37] mt-1 flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Sécurisation */}
      <section className="section-padding bg-[#0A0A0A] text-white" data-testid="gestion-securite">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div className="p-10 border border-gray-800 hover:border-[#D4AF37] transition-colors">
              <div className="w-12 h-12 flex items-center justify-center bg-[#D4AF37] text-[#0A0A0A] mb-6">
                <ShieldCheck size={22} />
              </div>
              <h2 className="font-serif text-2xl md:text-3xl mb-4">Une location sécurisée</h2>
              <p className="text-gray-400 leading-relaxed mb-4">La sécurisation des loyers fait partie intégrante de notre approche de la gestion locative.</p>
              <p className="text-gray-300 leading-relaxed">La <span className="text-[#D4AF37]">Garantie Loyers Impayés (GLI)</span> est intégrée à notre gestion locative afin de protéger vos revenus locatifs et de sécuriser votre investissement face aux éventuels impayés.</p>
            </div>
            <div className="p-10 border border-gray-800 hover:border-[#D4AF37] transition-colors">
              <div className="w-12 h-12 flex items-center justify-center bg-[#D4AF37] text-[#0A0A0A] mb-6">
                <FileText size={22} />
              </div>
              <h2 className="font-serif text-2xl md:text-3xl mb-4">Une assurance Propriétaire Non Occupant, à la carte</h2>
              <p className="text-gray-300 leading-relaxed">Parce que votre logement doit également être correctement assuré, nous pouvons vous accompagner dans la mise en place d’une <span className="text-[#D4AF37]">assurance Propriétaire Non Occupant (PNO)</span>, adaptée à votre situation et à votre bien.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Neuf */}
      <section className="section-padding" data-testid="gestion-neuf">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <p className="font-cinzel text-sm uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Investissement neuf</p>
              <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-4">Vous investissez dans le neuf ?</h2>
              <p className="text-[#0A0A0A] font-medium">Votre accompagnement peut commencer bien avant l’entrée du locataire.</p>
            </div>
            <div className="lg:col-span-7 space-y-5 text-gray-600 leading-relaxed">
              <p>Si vous réalisez un investissement locatif dans un programme neuf et souhaitez nous confier sa gestion, nous pouvons également vous accompagner lors des rendez-vous de pré-livraison et de livraison du logement.</p>
              <p>Nous pouvons ainsi vous aider à identifier les éventuelles réserves, suivre leur prise en charge et préparer la mise en location de votre bien.</p>
              <p className="border-l-2 border-[#D4AF37] pl-5 text-[#0A0A0A]">L’objectif : vous accompagner dès l’acquisition et jusqu’à la mise en location, puis assurer le suivi de votre investissement dans la durée.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Tranquillité & proximité */}
      <section className="section-padding bg-[#F9F9F9]" data-testid="gestion-engagements">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <div className="w-12 h-12 flex items-center justify-center bg-[#0A0A0A] text-[#D4AF37] mb-6">
                <Clock size={22} />
              </div>
              <h2 className="font-serif text-2xl md:text-3xl mb-5">Une gestion pensée pour votre tranquillité</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>Nous ne vous promettons pas qu’un investissement locatif sera toujours sans imprévu.</p>
                <p>En revanche, nous nous engageons à vous informer, vous conseiller et agir rapidement lorsque cela est nécessaire.</p>
                <p>Lorsqu’un travaux doit être envisagé, qu’un sinistre survient ou qu’une situation nécessite une attention particulière, nous vous en informons et assurons le suivi nécessaire.</p>
                <p>Chaque propriétaire et chaque investissement étant différent, notre accompagnement s’adapte à votre patrimoine et à vos objectifs.</p>
              </div>
            </div>
            <div>
              <div className="w-12 h-12 flex items-center justify-center bg-[#0A0A0A] text-[#D4AF37] mb-6">
                <Users size={22} />
              </div>
              <h2 className="font-serif text-2xl md:text-3xl mb-5">Une relation directe et de proximité</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>Chez MAINGUY Immobilier, nous avons fait le choix d’une agence à taille humaine.</p>
                <p>Vous bénéficiez d’un interlocuteur direct, disponible et impliqué dans la gestion de votre bien. Nous privilégions la proximité, la réactivité et une communication claire.</p>
                <p className="text-[#0A0A0A] font-medium">Notre mission est simple : vous permettre de rester propriétaire de votre bien sans avoir à en gérer toutes les contraintes.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Conclusion */}
      <section className="py-20 bg-[#0A0A0A]" data-testid="gestion-conclusion">
        <div className="container-custom text-center">
          <p className="font-serif text-2xl md:text-3xl text-white max-w-4xl mx-auto leading-relaxed">
            Confier votre gestion locative à <span className="text-[#D4AF37]">MAINGUY Immobilier</span>, c’est choisir un accompagnement de proximité pour louer, gérer et préserver votre patrimoine en toute sérénité.
          </p>
        </div>
      </section>

      <section id="gestion-form" className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-1">
              <h2 className="font-serif text-2xl mb-6">Pourquoi nous confier votre bien ?</h2>
              <ul className="space-y-4">
                {[
                  "Agence locale, indépendante et familiale",
                  "Estimation locative gratuite sous 48h",
                  "Honoraires transparents, sans frais cachés",
                  "Compte rendu de gérance mensuel",
                  "Un interlocuteur unique et joignable",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-gray-700">
                    <Check size={18} className="text-[#D4AF37] mt-0.5 flex-shrink-0" /> {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-2">
              <div className="bg-[#F9F9F9] p-8 md:p-12" data-testid="gestion-form">
                <h2 className="font-serif text-2xl mb-2">Demande de gestion locative</h2>
                <p className="text-gray-600 mb-8">Décrivez votre bien, nous vous recontactons pour une estimation locative gratuite.</p>
                <form onSubmit={handleSubmit} className="space-y-6" style={{ position: "relative" }}>
                  <HoneypotField />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="g-name">Nom complet *</Label>
                      <Input id="g-name" value={formData.name} required placeholder="Votre nom"
                        onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))} data-testid="gestion-form-name" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="g-email">Email *</Label>
                      <Input id="g-email" type="email" value={formData.email} required placeholder="votre@email.com"
                        onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))} data-testid="gestion-form-email" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="g-phone">Téléphone</Label>
                      <Input id="g-phone" type="tel" value={formData.phone} placeholder="06 00 00 00 00"
                        onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))} data-testid="gestion-form-phone" />
                    </div>
                    <div className="space-y-2">
                      <Label>Type de bien</Label>
                      <Select value={formData.property_type || undefined} onValueChange={(v) => setFormData((p) => ({ ...p, property_type: v }))}>
                        <SelectTrigger data-testid="gestion-form-type"><SelectValue placeholder="Sélectionnez" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Maison">Maison</SelectItem>
                          <SelectItem value="Appartement">Appartement</SelectItem>
                          <SelectItem value="Immeuble">Immeuble</SelectItem>
                          <SelectItem value="Local commercial">Local commercial</SelectItem>
                          <SelectItem value="Autre">Autre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="g-city">Commune du bien</Label>
                    <Input id="g-city" value={formData.city} placeholder="Le Loroux-Bottereau"
                      onChange={(e) => setFormData((p) => ({ ...p, city: e.target.value }))} data-testid="gestion-form-city" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="g-message">Votre bien *</Label>
                    <Textarea id="g-message" rows={5} value={formData.message} required
                      placeholder="Surface, nombre de pièces, état, bien déjà loué ou vacant..."
                      onChange={(e) => setFormData((p) => ({ ...p, message: e.target.value }))} data-testid="gestion-form-message" />
                  </div>
                  <Button type="submit" disabled={submitting}
                    className="w-full md:w-auto bg-[#0A0A0A] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0A0A] px-12"
                    data-testid="gestion-form-submit">
                    {submitting ? "Envoi en cours..." : (<><Send size={18} className="mr-2" /> Envoyer ma demande</>)}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GestionPage;
